import {
	BooleanInput,
	DateTimeInput,
	Form,
	SaveButton,
	TextInput,
	useRecordContext,
} from "react-admin";
import { FieldValues, SubmitHandler, useFormContext } from "react-hook-form";
import { MissionEventRecord } from "../types";
import { ReactNode, useEffect, useState } from "react";
import { eventMarked, isEta } from "./functions";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";

type EventDialogProps = {
	closeDialog: () => void;
	title: string;
	onSubmit: SubmitHandler<FieldValues>;
	children?: ReactNode;
};

type TimeType = "time" | "eta";

export const EventDialog = ({
	title,
	onSubmit,
	children,
	closeDialog,
}: EventDialogProps) => {
	const event = useRecordContext<MissionEventRecord>();
	const [timeType, setTimeType] = useState<TimeType>("time");
	const [eta, setEta] = useState<Date>(new Date());
	const [time, setTime] = useState<Date>(new Date());

	useEffect(() => {
		if (eventMarked(event)) {
			// editing already marked event from the timeline - populate form with event's data

			setTimeType(isEta(event) ? "eta" : "time");

			setEta(new Date(event.eta || new Date()));
			setTime(new Date(event.time || new Date()));
		}
	}, [event]); // eslint-disable-line react-hooks/exhaustive-deps

	if (!event) return null;

	const TimeInput = () => {
		const { setValue } = useFormContext();
		useEffect(() => {
			if (timeType === "eta") {
				setValue("time", null);
				setValue("eta", eta);
			} else if (timeType === "time") {
				setValue("time", time);
				setValue("eta", event.eta);
			}
		}, [setValue]);

		return (
			<>
				{timeType === "time" ? (
					<DateTimeInput source="time" defaultValue={time} fullWidth />
				) : (
					<DateTimeInput source="eta" defaultValue={eta} fullWidth />
				)}
			</>
		);
	};

	return (
		<Dialog open fullWidth maxWidth="sm">
			<DialogTitle>{title}</DialogTitle>
			<Form onSubmit={onSubmit} sanitizeEmptyValues>
				<DialogContent>
					{children}
					<RadioGroup
						row
						value={timeType}
						onChange={(_event, value) => setTimeType(value as TimeType)}
					>
						<FormControlLabel
							value="time"
							control={<Radio size="small" />}
							label="Event time"
						/>
						<FormControlLabel
							value="eta"
							control={<Radio size="small" />}
							label="ETA"
						/>
					</RadioGroup>

					<TimeInput />
					<TextInput
						label="Comments (optional)"
						source="comments"
						multiline
						fullWidth
						rows={3}
					/>
					<BooleanInput source="skip_notifications" />
				</DialogContent>
				<DialogActions>
					<Button size="small" onClick={closeDialog}>
						Cancel
					</Button>
					<SaveButton variant="contained" size="small" alwaysEnable />
				</DialogActions>
			</Form>
		</Dialog>
	);
};
